import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router';
import {
    FaArrowLeft,
    FaUser,
    FaPhone,
    FaEnvelope,
    FaCalendarAlt,
    FaMapMarkerAlt,
    FaGraduationCap,
    FaBriefcase,
    FaUserTie,
    FaCheckCircle,
    FaTimesCircle,
    FaClock,
    FaPrint,
    FaEdit,
    FaIdCard,
    FaImage,
    FaVenusMars,
    FaTint,
    FaHome,
    FaBuilding,
    FaGlobe,
    FaUserFriends,
    FaFileExcel
} from 'react-icons/fa';
import { useDispatch } from "react-redux";
import { openModal } from "../../redux/features/modalSlice";
import { useGetSadasyaByIdQuery } from '../../redux/services/sadsayamember/sadsya.Services';
import * as XLSX from 'xlsx';

function SadasyaView() {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const {
        data: userData,
        isLoading,
        isError,
        error,
    } = useGetSadasyaByIdQuery(id);

    // State to track image load errors
    const [imageErrors, setImageErrors] = useState({
        profile: false,
        aadharFront: false,
        aadharBack: false
    });

    // Loading state
    if (isLoading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    // Error state
    if (isError) {
        return (
            <div className="alert alert-danger m-3">
                Error: {error?.data?.message || 'Failed to load sadasya details'}
            </div>
        );
    }

    const user = userData?.data;

    if (!user) {
        return (
            <div className="alert alert-warning m-3">
                Sadasya member not found
            </div>
        );

    }

    // Get status info
    const getStatusInfo = (status) => {
        switch(status) {
            case '1':
                return { label: 'Approved', color: 'success', icon: <FaCheckCircle /> };
            case '0':
                return { label: 'Pending', color: 'warning', icon: <FaClock /> };
            case '2':
                return { label: 'Rejected', color: 'danger', icon: <FaTimesCircle /> };
            default:
                return { label: 'Unknown', color: 'secondary', icon: null };
        }
    };

    const statusInfo = getStatusInfo(user.status);

    // Helper function to get full image URL
    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
            return imagePath;
        }
        const baseUrl = user.imageBaseurl || 'http://localhost:5000';
        const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
        return `${baseUrl}/uploads/family/${cleanPath}`;
    };

    // Handle image load errors
    const handleImageError = (type) => {
        setImageErrors(prev => ({
            ...prev,
            [type]: true
        }));
    };

    // Export to Excel function
    const exportToExcel = () => {
        // Create a flat object with all user data
        const exportData = {
            'ID': user.id || '-',
            'Name': user.name || '-',
            'Sadasya ID': user.sadasya_unique_id || '-',
            'Pariwar ID': user.pariwar_unique_id || '-',
            'Gender': user.gender || '-',
            'Father Name': user.father_name || '-',
            'Mother Name': user.mother_name || '-',
            'Date of Birth': user.dob ? new Date(user.dob).toLocaleDateString() : '-',
            'Marriage Status': user.marrige_status || '-',
            'Marriage Year': user.marrige_year ? new Date(user.marrige_year).getFullYear() : '-',
            'Blood Group': user.blood_group || '-',
            'Mobile': user.mobile || '-',
            'WhatsApp': user.wp_number || '-',
            'Email': user.email || '-',
            'Aadhar Number': user.aadhar_number || '-',
            'Education': user.education || '-',
            'Business': user.business || '-',
            'Job Position': user.job_position || '-',
            'Department': user.department || '-',
            'Posted Place': user.posted_place || '-',
            'Working Address': user.working_address || '-',
            'Annual Income': user.anual_income ? `₹${parseInt(user.anual_income).toLocaleString()}` : '-',
            'Relations': user.relations || '-',
            'Self Gotra': user.self_gotra || '-',
            'Sadasya Gotra': user.sadasya_gotra || '-',
            'Rishi Pravar Gotra': user.rishi_pravar_gotra || '-',
            'Mother Gotra': user.mother_gotra || '-',
            'Dadi Gotra': user.dadi_gotra || '-',
            'Nani Gotra': user.nani_gotra || '-',
            'Dada Name': user.dada_name || '-',
            'Dadi Name': user.dadi_name || '-',
            'Nana Name': user.nana_name || '-',
            'Nani Name': user.nani_name || '-',
            'Father-in-Law': user.father_in_law_name || '-',
            'Mother-in-Law': user.mother_in_law_name || '-',
            'Husband Name': user.husband_name || '-',
            'Father Address': user.father_address || '-',
            'Country': user.country_name || '-',
            'State': user.state_name || '-',
            'City': user.city_name || '-',
            'Tehsil': user.tehsil_name || '-',
            'Village': user.village_name || '-',
            'Ward': user.ward_name_number || '-',
            'Pin Code': user.pin_code || '-',
            'Native Village': user.native_village || '-',
            'International Address': user.international_address || '-',
            'Location Latitude': user.location_latitude || '-',
            'Location Longitude': user.location_longitude || '-',
            'Location Accuracy': user.location_accuracy || '-',
            'Location Address': user.location_address || '-',
            'Location Timestamp': user.location_timestamp ? new Date(user.location_timestamp).toLocaleString() : '-',
            'Status': user.status_label || '-',
            'Created At': user.created_at ? new Date(user.created_at).toLocaleString() : '-',
            'Updated At': user.updated_at ? new Date(user.updated_at).toLocaleString() : '-'
        };

        // Convert to array for Excel
        const dataArray = [exportData];
        
        // Create workbook
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(dataArray);
        
        // Set column widths
        const colWidths = [];
        Object.keys(exportData).forEach((key, index) => {
            colWidths[index] = { wch: Math.max(key.length, 20) };
        });
        ws['!cols'] = colWidths;
        
        XLSX.utils.book_append_sheet(wb, ws, 'Sadasya Details');
        
        // Generate Excel file
        const fileName = `Sadasya_${user.name || 'member'}_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    return (
        <div>
            {/* =============================================
                PAGE HEADER
                ============================================= */}
            <div className="page-header-breadcrumb all_breadcrumb">
                <div className="ms-md-1 mb-1 mb-md-0 ms-0 page_heading_breadcrumb">
                    <nav>
                        <h4 className="fw-semibold">
                            <FaUser className="me-2" />
                            Sadasya Member Details
                        </h4>
                        <ol className="breadcrumb mb-0">
                            <li className="breadcrumb-item">
                                <Link to="/">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <Link to="/sadasya-members">Sadasya Members</Link>
                            </li>
                            <li className="breadcrumb-item active" aria-current="page">
                                #{id}
                            </li>
                        </ol>
                    </nav>
                </div>
                <div className="page-title fw-semibold fs-18 mb-0">
                    <button 
                        className="btn btn-secondary me-2"
                        onClick={() => navigate(-1)}
                    >
                        <FaArrowLeft className="me-2" />
                        Back
                    </button>
                    <button 
                        className="btn btn-success me-2"
                        onClick={exportToExcel}
                    >
                        <FaFileExcel className="me-2" />
                        Download Excel
                    </button>
                    <button 
                        className="btn btn-primary me-2"
                        onClick={() => window.print()}
                    >
                        <FaPrint className="me-2" />
                        Print
                    </button>
                    <button 
                        className="btn btn-warning"
                        onClick={() => {
                            dispatch(
                                openModal({
                                    type: "edit-sadasya-member",
                                    title: "Edit Sadasya Member",
                                    data: user
                                })
                            );
                        }}
                    >
                        <FaEdit className="me-2" />
                        Edit
                    </button>
                </div>
            </div>

            {/* =============================================
                MEMBER PROFILE
                ============================================= */}
            <div className="row g-4">
                {/* Left Column - Profile Image & Basic Info */}
                <div className="col-lg-4">
                    <div className="card shadow-sm">
                        <div className="card-body text-center">
                            <div className="mb-3">
                                {user.p_image && !imageErrors.profile ? (
                                    <img
                                        src={getImageUrl(user.p_image)}
                                        alt={user.name}
                                        className="rounded-circle"
                                        style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                                        onError={() => handleImageError('profile')}
                                    />
                                ) : (
                                    <div 
                                        className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center mx-auto"
                                        style={{ width: '150px', height: '150px', fontSize: '60px' }}
                                    >
                                        {user.name?.charAt(0)?.toUpperCase() || 'S'}
                                    </div>
                                )}
                            </div>
                            <h4 className="fw-bold mb-1">{user.name || '-'}</h4>
                            <p className="text-muted mb-2">
                                <FaUserTie className="me-1" />
                                Sadasya ID: {user.sadasya_unique_id || 'N/A'}
                            </p>
                            <div className="mt-3">
                                <span className={`badge bg-${statusInfo.color} fs-6 px-3 py-2`}>
                                    {statusInfo.icon} {statusInfo.label}
                                </span>
                            </div>
                            <hr />
                            <div className="text-start">
                                <p className="mb-2">
                                    <strong>Pariwar ID:</strong> {user.pariwar_unique_id || 'N/A'}
                                </p>
                                <p className="mb-2">
                                    <strong>Role:</strong> {user.roles === 2 ? 'Sadasya' : 'Member'}
                                </p>
                                <p className="mb-2">
                                    <strong>Member Since:</strong> {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                                </p>
                                <p className="mb-0">
                                    <strong>Last Updated:</strong> {user.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'N/A'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column - Detailed Information */}
                <div className="col-lg-8">
                    {/* Personal Information */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-header bg-primary text-white">
                            <h5 className="mb-0"><FaUser className="me-2" />Personal Information</h5>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="text-muted small">Full Name</label>
                                    <p className="fw-bold mb-0">{user.name || '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small"><FaVenusMars className="me-1" /> Gender</label>
                                    <p className="mb-0">{user.gender || '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small">Sadasya ID</label>
                                    <p className="fw-bold mb-0">{user.sadasya_unique_id || '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small">Father's Name</label>
                                    <p className="mb-0">{user.father_name || '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small">Mother's Name</label>
                                    <p className="mb-0">{user.mother_name || '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small">Date of Birth</label>
                                    <p className="mb-0">{user.dob ? new Date(user.dob).toLocaleDateString() : '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small"><FaTint className="me-1" /> Blood Group</label>
                                    <p className="mb-0">{user.blood_group || '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small">Marriage Status</label>
                                    <p className="mb-0">{user.marrige_status || '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small">Marriage Year</label>
                                    <p className="mb-0">{user.marrige_year ? new Date(user.marrige_year).getFullYear() : '-'}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Family Relations */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-header" style={{ backgroundColor: '#20c997', color: 'white' }}>
                            <h5 className="mb-0"><FaUserFriends className="me-2" />Family Relations</h5>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <label className="text-muted small">Relations</label>
                                    <p className="fw-bold mb-0">{user.relations || '-'}</p>
                                </div>
                                <div className="col-md-4">
                                    <label className="text-muted small">Father-in-Law</label>
                                    <p className="mb-0">{user.father_in_law_name || '-'}</p>
                                </div>
                                <div className="col-md-4">
                                    <label className="text-muted small">Mother-in-Law</label>
                                    <p className="mb-0">{user.mother_in_law_name || '-'}</p>
                                </div>
                                <div className="col-md-4">
                                    <label className="text-muted small">Husband Name</label>
                                    <p className="mb-0">{user.husband_name || '-'}</p>
                                </div>
                                <div className="col-md-4">
                                    <label className="text-muted small">Father Address</label>
                                    <p className="mb-0">{user.father_address || '-'}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Contact Information */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-header bg-info text-white">
                            <h5 className="mb-0"><FaPhone className="me-2" />Contact Information</h5>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="text-muted small">Mobile Number</label>
                                    <p className="fw-bold mb-0">{user.mobile || '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small">WhatsApp Number</label>
                                    <p className="mb-0">{user.wp_number || '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small"><FaEnvelope className="me-1" /> Email</label>
                                    <p className="mb-0">{user.email || '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small">Aadhar Number</label>
                                    <p className="mb-0">{user.aadhar_number || '-'}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Aadhar Card Images */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-header" style={{ backgroundColor: '#6f42c1', color: 'white' }}>
                            <h5 className="mb-0"><FaIdCard className="me-2" />Aadhar Card Images</h5>
                        </div>
                        <div className="card-body">
                            <div className="row g-4">
                                <div className="col-md-6">
                                    <label className="text-muted small mb-2 d-block">
                                        <FaImage className="me-1" /> Front Side
                                    </label>
                                    {user.aadhar_front && !imageErrors.aadharFront ? (
                                        <div className="border rounded p-2 text-center" style={{ backgroundColor: '#f8f9fa' }}>
                                            <img
                                                src={getImageUrl(user.aadhar_front)}
                                                alt="Aadhar Card Front"
                                                className="img-fluid"
                                                style={{ 
                                                    maxHeight: '250px', 
                                                    objectFit: 'contain',
                                                    width: '100%'
                                                }}
                                                onError={() => handleImageError('aadharFront')}
                                            />
                                            <div className="mt-2">
                                                <a 
                                                    href={getImageUrl(user.aadhar_front)}
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm btn-outline-primary me-1"
                                                >
                                                    <FaImage className="me-1" /> View Full
                                                </a>
                                                <a 
                                                    href={getImageUrl(user.aadhar_front)}
                                                    download
                                                    className="btn btn-sm btn-outline-success"
                                                >
                                                    Download
                                                </a>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="border rounded p-4 text-center text-muted bg-light">
                                            <FaIdCard className="display-4 d-block mb-2" style={{ fontSize: '48px' }} />
                                            <p className="mb-0">{imageErrors.aadharFront ? 'Failed to load image' : 'No front image uploaded'}</p>
                                        </div>
                                    )}
                                </div>

                                <div className="col-md-6">
                                    <label className="text-muted small mb-2 d-block">
                                        <FaImage className="me-1" /> Back Side
                                    </label>
                                    {user.aadhar_back && !imageErrors.aadharBack ? (
                                        <div className="border rounded p-2 text-center" style={{ backgroundColor: '#f8f9fa' }}>
                                            <img
                                                src={getImageUrl(user.aadhar_back)}
                                                alt="Aadhar Card Back"
                                                className="img-fluid"
                                                style={{ 
                                                    maxHeight: '250px', 
                                                    objectFit: 'contain',
                                                    width: '100%'
                                                }}
                                                onError={() => handleImageError('aadharBack')}
                                            />
                                            <div className="mt-2">
                                                <a 
                                                    href={getImageUrl(user.aadhar_back)}
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm btn-outline-primary me-1"
                                                >
                                                    <FaImage className="me-1" /> View Full
                                                </a>
                                                <a 
                                                    href={getImageUrl(user.aadhar_back)}
                                                    download
                                                    className="btn btn-sm btn-outline-success"
                                                >
                                                    Download
                                                </a>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="border rounded p-4 text-center text-muted bg-light">
                                            <FaIdCard className="display-4 d-block mb-2" style={{ fontSize: '48px' }} />
                                            <p className="mb-0">{imageErrors.aadharBack ? 'Failed to load image' : 'No back image uploaded'}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Gotra & Family Information */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-header bg-success text-white">
                            <h5 className="mb-0"><FaUserTie className="me-2" />Gotra & Family Information</h5>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <label className="text-muted small">Self Gotra</label>
                                    <p className="fw-bold mb-0">{user.self_gotra || '-'}</p>
                                </div>
                                <div className="col-md-4">
                                    <label className="text-muted small">Sadasya Gotra</label>
                                    <p className="mb-0">{user.sadasya_gotra || '-'}</p>
                                </div>
                                <div className="col-md-4">
                                    <label className="text-muted small">Rishi Pravar Gotra</label>
                                    <p className="mb-0">{user.rishi_pravar_gotra || '-'}</p>
                                </div>
                                <div className="col-md-4">
                                    <label className="text-muted small">Mother Gotra</label>
                                    <p className="mb-0">{user.mother_gotra || '-'}</p>
                                </div>
                                <div className="col-md-4">
                                    <label className="text-muted small">Dadi Gotra</label>
                                    <p className="mb-0">{user.dadi_gotra || '-'}</p>
                                </div>
                                <div className="col-md-4">
                                    <label className="text-muted small">Nani Gotra</label>
                                    <p className="mb-0">{user.nani_gotra || '-'}</p>
                                </div>
                                <div className="col-md-4">
                                    <label className="text-muted small">Dada Name</label>
                                    <p className="mb-0">{user.dada_name || '-'}</p>
                                </div>
                                <div className="col-md-4">
                                    <label className="text-muted small">Dadi Name</label>
                                    <p className="mb-0">{user.dadi_name || '-'}</p>
                                </div>
                                <div className="col-md-4">
                                    <label className="text-muted small">Nana Name</label>
                                    <p className="mb-0">{user.nana_name || '-'}</p>
                                </div>
                                <div className="col-md-4">
                                    <label className="text-muted small">Nani Name</label>
                                    <p className="mb-0">{user.nani_name || '-'}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Address Information */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-header bg-secondary text-white">
                            <h5 className="mb-0"><FaMapMarkerAlt className="me-2" />Address Information</h5>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <label className="text-muted small"><FaGlobe className="me-1" /> Country</label>
                                    <p className="mb-0">{user.country_name || '-'}</p>
                                </div>
                                <div className="col-md-4">
                                    <label className="text-muted small">State</label>
                                    <p className="mb-0">{user.state_name || '-'}</p>
                                </div>
                                <div className="col-md-4">
                                    <label className="text-muted small">City</label>
                                    <p className="mb-0">{user.city_name || '-'}</p>
                                </div>
                                <div className="col-md-4">
                                    <label className="text-muted small">Tehsil</label>
                                    <p className="mb-0">{user.tehsil_name || '-'}</p>
                                </div>
                                <div className="col-md-4">
                                    <label className="text-muted small"><FaHome className="me-1" /> Village</label>
                                    <p className="mb-0">{user.village_name || '-'}</p>
                                </div>
                                <div className="col-md-4">
                                    <label className="text-muted small"><FaBuilding className="me-1" /> Ward</label>
                                    <p className="mb-0">{user.ward_name_number || '-'}</p>
                                </div>
                                <div className="col-md-4">
                                    <label className="text-muted small">Pin Code</label>
                                    <p className="mb-0">{user.pin_code || '-'}</p>
                                </div>
                                <div className="col-md-4">
                                    <label className="text-muted small">Native Village</label>
                                    <p className="mb-0">{user.native_village || '-'}</p>
                                </div>
                                <div className="col-md-4">
                                    <label className="text-muted small">International Address</label>
                                    <p className="mb-0">{user.international_address || '-'}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Professional Information */}
                    <div className="card shadow-sm mb-4">
                        <div className="card-header bg-dark text-white">
                            <h5 className="mb-0"><FaBriefcase className="me-2" />Professional Information</h5>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="text-muted small"><FaGraduationCap className="me-1" /> Education</label>
                                    <p className="mb-0">{user.education || '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small">Business</label>
                                    <p className="mb-0">{user.business || '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small">Job Position</label>
                                    <p className="mb-0">{user.job_position || '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small">Department</label>
                                    <p className="mb-0">{user.department || '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small">Posted Place</label>
                                    <p className="mb-0">{user.posted_place || '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small">Working Address</label>
                                    <p className="mb-0">{user.working_address || '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small">Annual Income</label>
                                    <p className="fw-bold mb-0">{user.anual_income ? `₹${parseInt(user.anual_income).toLocaleString()}` : '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small">Relations</label>
                                    <p className="mb-0">{user.relations || '-'}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Location Information */}
                    <div className="card shadow-sm">
                        <div className="card-header" style={{ backgroundColor: '#17a2b8', color: 'white' }}>
                            <h5 className="mb-0"><FaMapMarkerAlt className="me-2" />Location Information</h5>
                        </div>
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="text-muted small">Latitude</label>
                                    <p className="mb-0">{user.location_latitude || '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small">Longitude</label>
                                    <p className="mb-0">{user.location_longitude || '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small">Location Accuracy</label>
                                    <p className="mb-0">{user.location_accuracy || '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small">Location Address</label>
                                    <p className="mb-0">{user.location_address || '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small">Location Timestamp</label>
                                    <p className="mb-0">{user.location_timestamp ? new Date(user.location_timestamp).toLocaleString() : '-'}</p>
                                </div>
                                <div className="col-md-6">
                                    <label className="text-muted small">GPS Location Image</label>
                                    <p className="mb-0">{user.gps_location_image || 'Not available'}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SadasyaView;